import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getCookieDomainCandidates, useCookieOptions } from '~/composables/useCookieOptions';
import {
  AUTH_COOKIE_MAX_AGE,
  LEGACY_AUTH_COOKIE_KEYS,
  STORAGE_KEYS,
} from '~/constants/storageKeys';
import { isJwtExpired } from '~/utils/jwt';

export const useAuthStore = defineStore('auth', () => {
  /**
   * 【這三個刻意包成 writable computed，不要改回裸 ref】
   *
   * setup store 回傳的裸 ref 就是 store 的 state，而 `@pinia/nuxt` 在伺服器端會做
   * `nuxtApp.payload.pinia = pinia.state.value` —— 也就是說 state 會被序列化進
   * SSR 的 HTML（`__NUXT_DATA__`）。下面的 `isLoggedIn()` 又會在 SSR 就把 cookie 的值
   * 讀進來，結果是 **access token 與 refresh token 明文出現在頁面原始碼裡**，
   * 任何會保存 HTML 的環節（CDN／edge cache、error monitoring、session replay）都會收走。
   *
   * pinia 只把「非 computed 的 ref」納入 state，所以包一層 writable computed 之後
   * 這三個值就不再進 payload，而 `store.token` 的讀寫語意完全不變。
   * 真正的儲存體是 cookie，這裡只是本頁的快取。
   */
  const tokenState = ref('');
  const refreshTokenState = ref('');
  const csrfTokenState = ref('');

  const token = computed({
    get: () => tokenState.value,
    set: (value: string) => {
      tokenState.value = value;
    },
  });
  const refreshToken = computed({
    get: () => refreshTokenState.value,
    set: (value: string) => {
      refreshTokenState.value = value;
    },
  });
  const csrfToken = computed({
    get: () => csrfTokenState.value,
    set: (value: string) => {
      csrfTokenState.value = value;
    },
  });

  const COOKIE_MAX_AGE = AUTH_COOKIE_MAX_AGE;

  // 使用統一的 cookie 選項（host-only，不跨子網域）
  // 7天過期時間，確保持久性
  // 注意：不設定 default，避免在沒有值時自動創建空值 cookie
  // 只有在明確設定值時才會創建 cookie
  const cookieOptions = useCookieOptions<string>({
    maxAge: COOKIE_MAX_AGE,
  });

  // 使用 useCookie，不設定 default，避免自動創建空值 cookie
  // 型別帶 null 是為了能真正「刪除」cookie：Nuxt 的 writeClientCookie 只有在值為
  // null / undefined 時才會加上 max-age=-1 送出刪除，給空字串只會寫一顆空值 cookie。
  const tokenCookie = useCookie<string | null>(STORAGE_KEYS.TOKEN, cookieOptions);
  const refreshTokenCookie = useCookie<string | null>(STORAGE_KEYS.REFRESH_TOKEN, cookieOptions);

  /**
   * 取得 access token
   * 即使已過期也照樣回傳：讓請求打出去換一個 401，由 useApiFetch 走 refresh 流程續期。
   * 真正決定「還算不算登入」的是 refresh token（見 isLoggedIn）。
   *
   * 比照 getRefreshToken 以 `document.cookie` 的當下值為準，而不是只讀 store 的 ref。
   * ref 只反映「本頁載入時讀到的值」與「本頁自己的寫入」，有兩條路會讓它變成舊的：
   *   1. 別的分頁換過 token —— 那邊只寫進 cookie，本頁的 ref 不會更新
   *   2. SSR 階段 isLoggedIn() 讀 cookie 寫進 ref，這個值會被序列化進 payload 帶到
   *      瀏覽器；只要伺服器當下讀到的不是最新那份，client 就會一路沿用那個舊值
   * 而 `document.cookie` 讀不到 HttpOnly，拿到的必然是前端自己寫的、也就是最新那份。
   */
  const getToken = () => {
    if (!isLoggedIn()) return '';

    const latest = readCookieNow(STORAGE_KEYS.TOKEN);
    // 讀不到就沿用 ref：cookie 寫入失敗（例如超過長度上限）時，
    // 手上這張雖然舊但還能用，總比送出一個沒有 Authorization 的請求好
    if (latest && latest !== token.value) {
      token.value = latest;
    }

    return token.value;
  };

  /**
   * 直接從 document.cookie 讀當下的值。
   *
   * useCookie 的 ref 只反映「本頁載入時讀到的值」與「本頁自己的寫入」，
   * 別的分頁換過 token 之後它不會更新。
   */
  const readCookieNow = (name: string): string => {
    if (typeof document === 'undefined') return '';
    const hit = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${name}=`));
    if (!hit) return '';
    try {
      return decodeURIComponent(hit.slice(name.length + 1));
    } catch {
      return hit.slice(name.length + 1);
    }
  };

  /**
   * 取得 refresh token
   *
   * 已過期就回空字串，避免拿著死掉的 refresh token 反覆去換（後端會回 422），
   * 造成 refresh 失敗 → 登出 → 整頁跳轉 → 重新載入後又以為已登入的無窮迴圈。
   *
   * 一律以 cookie 的當下值為準：refresh token 是一次性的，換過就作廢。
   * 若使用者開了多個分頁，另一個分頁換完新 token 只會寫進 cookie，
   * 本頁 store 裡那份仍是舊的——拿它去換必定得到 422「刷新令牌已過期」，
   * 使用者就會在明明還登入著的情況下被踢回登入頁。
   */
  const getRefreshToken = () => {
    const latest = readCookieNow(STORAGE_KEYS.REFRESH_TOKEN);
    if (latest && latest !== refreshToken.value) {
      refreshToken.value = latest;
    }

    const value = latest || refreshToken.value || refreshTokenCookie.value || '';
    if (!value || isJwtExpired(value)) {
      return '';
    }
    return value;
  };

  const getCsrfToken = () => {
    if (csrfToken.value) {
      return csrfToken.value;
    }
    if (import.meta.client) {
      const storedToken = localStorage.getItem(STORAGE_KEYS.CSRF_TOKEN);
      if (storedToken) {
        csrfToken.value = storedToken;
        return storedToken;
      }
    }
    return '';
  };

  /**
   * 是否仍有有效的登入 session
   *
   * 判斷基準是 refresh token 而非 access token：access token 過期是常態，可以自動續期；
   * refresh token 過期才代表 session 真的結束了。
   *
   * 若 token 不是 JWT / 沒有 exp，isJwtExpired 會回 false（保守視為未過期），
   * 行為與過去相同，僅檢查是否存在。
   */
  const isLoggedIn = () => {
    // 檢查 store 中是否有 token 和 refreshToken
    if (token.value && refreshToken.value) {
      return !isJwtExpired(refreshToken.value);
    }

    // 檢查 cookies 中是否有 token 和 refreshToken（從之前的登入保存的）
    // 如果 cookie 存在且有值，則使用它
    if (tokenCookie.value && refreshTokenCookie.value) {
      if (isJwtExpired(refreshTokenCookie.value)) {
        return false;
      }
      token.value = tokenCookie.value;
      refreshToken.value = refreshTokenCookie.value;
      return true;
    }

    return false;
  };

  /**
   * 設定 token 和 refreshToken
   * Cookie 為 host-only（不設定 domain），因此各環境（app / alpha / localhost）
   * 各自持有自己的登入狀態，不會互相汙染。
   */
  const setTokens = (_token: string, _refreshToken: string | null) => {
    // 更新 store 中的值
    token.value = _token;
    if (_refreshToken) {
      refreshToken.value = _refreshToken;
    }

    // 更新 cookie
    // 只有在有值時才設定 cookie，避免創建空值 cookie
    if (_token) {
      tokenCookie.value = _token;
    }
    if (_refreshToken) {
      refreshTokenCookie.value = _refreshToken;
    }

    if (import.meta.client) {
      // 與 clearTokens 對稱地再同步寫一次：Nuxt 的 useCookie 是透過 watcher（flush: 'pre'）
      // 非同步寫入 document.cookie 的，若在 flush 之前就整頁跳轉／重載，新的 token 會遺失，
      // 下次啟動又拿舊的那張 refresh token 去換——而它已經被消費掉，後端回
      // 422「刷新令牌已過期」，使用者就莫名其妙被踢回登入頁。
      if (_token) {
        document.cookie = `${STORAGE_KEYS.TOKEN}=${encodeURIComponent(_token)}; path=/; max-age=${COOKIE_MAX_AGE}`;
      }
      if (_refreshToken) {
        document.cookie = `${STORAGE_KEYS.REFRESH_TOKEN}=${encodeURIComponent(_refreshToken)}; path=/; max-age=${COOKIE_MAX_AGE}`;
      }
    }
  };

  const setCsrfToken = (_csrfToken: string) => {
    csrfToken.value = _csrfToken;
    if (import.meta.client) {
      if (_csrfToken) {
        localStorage.setItem(STORAGE_KEYS.CSRF_TOKEN, _csrfToken);
      } else {
        localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
      }
    }
  };

  const clearCsrfToken = () => {
    csrfToken.value = '';
    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);
    }
  };

  /**
   * 清除殘留在祖先網域（.momentx.ai 等）上的舊版認證 cookie
   *
   * 這些是改成 host-only 之前寫入的。若不清掉，會與新的 host-only cookie 同名並存，
   * 瀏覽器兩份都會送出，前端讀 document.cookie 也會拿到不確定的那一份，
   * 導致「清了還在、登出後又像登入」的狀況。
   *
   * 舊名稱（Authorization / RefreshToken）一併清：前端已經不讀它們，但那是後端在
   * Google 登入時寫的名字，殘留在父網域上沒有意義。HttpOnly 的那份這裡刪不動，
   * 只有登出的 server route 刪得掉。
   *
   * 【要掃過每一層祖先，不能只清末兩段】`a.b.momentx.ai` 上寫在 `.b.momentx.ai` 的
   * 那份，只清 `.momentx.ai` 是刪不掉的。這件事在 Google 登入前特別要緊：
   * `handleSocialLogin` 就是靠這裡把舊名稱清乾淨，回調頁上的交接才只會看到後端剛寫的
   * 那一份（見 server/routes/_auth/oauth-handoff.post.ts）。範圍與
   * `plugins/00.authCookieMigration.client.ts` 對齊，兩邊都用 getCookieDomainCandidates。
   */
  const clearLegacyDomainCookies = () => {
    if (!import.meta.client) return;

    const domains = getCookieDomainCandidates(window.location.hostname);
    if (domains.length === 0) return;

    const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    for (const key of [
      STORAGE_KEYS.TOKEN,
      STORAGE_KEYS.REFRESH_TOKEN,
      STORAGE_KEYS.ORGUUID,
      LEGACY_AUTH_COOKIE_KEYS.TOKEN,
      LEGACY_AUTH_COOKIE_KEYS.REFRESH_TOKEN,
    ]) {
      for (const domain of domains) {
        document.cookie = `${key}=; ${expired}; domain=${domain}`;
      }
    }
  };

  const clearTokens = () => {
    token.value = '';
    refreshToken.value = '';
    csrfToken.value = '';

    // 設為 null 才會真正刪除 cookie（Nuxt 會送出 max-age=-1）；
    // 設空字串只會留下一顆空值 cookie 並把 max-age 續回 7 天。
    tokenCookie.value = null;
    refreshTokenCookie.value = null;

    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEYS.CSRF_TOKEN);

      // Nuxt 的 useCookie 是透過 watcher 寫入 document.cookie 的（flush: 'pre'，非同步），
      // 這裡再同步刪一次，確保就算後續立刻整頁跳轉也已經清乾淨。
      const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      document.cookie = `${STORAGE_KEYS.TOKEN}=; ${expired}`;
      document.cookie = `${STORAGE_KEYS.REFRESH_TOKEN}=; ${expired}`;
      document.cookie = `${STORAGE_KEYS.ORGUUID}=; ${expired}`;
      // 舊名稱的 host-only 殘留（遷移 plugin 通常已經清掉，這裡補一刀）
      document.cookie = `${LEGACY_AUTH_COOKIE_KEYS.TOKEN}=; ${expired}`;
      document.cookie = `${LEGACY_AUTH_COOKIE_KEYS.REFRESH_TOKEN}=; ${expired}`;

      // 一併清掉舊版跨子網域的殘留
      clearLegacyDomainCookies();
    }
  };

  const reset = () => {
    clearTokens();
  };

  return {
    token,
    refreshToken,
    csrfToken,
    getToken,
    getRefreshToken,
    getCsrfToken,
    isLoggedIn,
    setTokens,
    setCsrfToken,
    clearTokens,
    clearCsrfToken,
    clearLegacyDomainCookies,
    reset,
  };
});
